import { makeStyles, Popover } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { Check, ChevronDown, Search, X } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Input } from './Input';
import { Checkbox } from './Toggle';

const useSelectClasses = makeStyles({
    toggle: {
        position: 'sticky',
        right: 0,
        width: 10,
        height: 10,
        transition: `transform ${theme.transitions.fast}s`,
        '&.open': { transform: 'scaleY(-1)' },
    },
    popover: {
        display: 'flex',
        flexDirection: 'column',
        overflow: ' auto',
        '@supports (overflow: overlay)': { '&': { overflow: 'overlay' } },
        borderRadius: 8,
        marginTop: 8,
        maxHeight: 400,
        '& > hr': { borderColor: theme.colors.neutral[4] },
    },
    item: {
        ':where(&)': {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 25px',
            cursor: 'pointer',
            color: theme.colors.neutral[2],
            ...theme.fonts.button,
            ':where(&:hover)': { background: '#EBEDF5' },
            ':where(&.selected, &:hover, &.selected > span, &:hover > span)': { color: theme.colors.darkBlue[5] },
            ':where(&.multi > span:first-child)': { margin: '-12px' },
            ':where(& > svg)': { width: 16, height: 16 },
        },
    },
    filter: {
        ':where(&)': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 16,
            backgroundColor: 'white',
        },
    },
});

/**
 * Custom select based on the Input component.
 * This component implements several features such as multi-item selection, multi toggling and search options.
 * Different from other input components, the select component is required to be always controlled.
 *
 * @param props.selected boolean array containing `true` on the indices that are selected
 * @param props.options list of options to be selected
 * @param props.multi enable multi option selection (a checkbox is displayed beside each option)
 * @param props.all if `props.multi === true` display select-all and unselect-all options
 * @param props.unselect if `props.multi === false` this option will allow a single selected option to be unselected
 * @param props.render function to render the component, the `on` parameter indicates where the option will be rendered
 * @param props.search function that takes an option and return a string, if provided the search input is enabled
 * @param props.searchLabel label displayed in the search input
 * @param props.onSelected callback triggered when the list of selected items change
 */
export const Select = <T extends any>(
    props: {
        selected: boolean[];
        options: T[];
        multi?: boolean;
        all?: boolean;
        unselect?: boolean;
        render?: (option: T, on: 'input' | 'popup') => React.ReactNode;
        search?: (option: T) => string;
        searchLabel?: string;
        onSelected?: (option: boolean[]) => void;
        defaultValue?: never;
        value?: never;
        type?: never;
    } & Parameters<typeof Input>[0],
) => {
    const { selected, options, unselect, multi, all, render, search, searchLabel, onSelected, ...inputProps } = props;
    const classes = useSelectClasses();
    const { t } = usePrefixTranslation('global', { global: 'forms.Select' });
    const anchor$ = React.useRef<HTMLElement>();
    const [open, setOpen] = React.useState(false);
    const [filter, setFilter] = React.useState('');
    const selectedOptions = options
        .filter((_, i) => selected[i])
        .map(option => render?.(option, 'input') ?? <span>{String(option)}</span>);
    const cls = { open, multi };

    return (
        <Input
            {...inputProps}
            readOnly
            placeholder={selectedOptions.length === 0 ? inputProps.placeholder : ''}
            left={
                <>
                    {inputProps.left}
                    {selectedOptions}
                </>
            }
            right={<ChevronDown className={clsx(classes.toggle, cls)} />}
            insideProps={{
                ...inputProps?.insideProps,
                ref: anchor$ as any,
                className: clsx(cls, inputProps.insideProps?.className),
                onClick: e => (setOpen(true), inputProps?.insideProps?.onClick?.(e)),
            }}
            bottom={
                <Popover
                    classes={{ paper: classes.popover }}
                    PaperProps={{ style: { minWidth: anchor$.current?.clientWidth ?? 0 } }}
                    open={open}
                    onClose={() => setOpen(false)}
                    anchorEl={anchor$.current}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    disableScrollLock
                    onClick={e => !e.defaultPrevented && (setOpen(false), setFilter(''))}
                >
                    {multi &&
                        all &&
                        [
                            { label: t('select-all'), icon: <Check />, value: true },
                            { label: t('unselect-all'), icon: <X />, value: false },
                        ].map(({ label, icon, value }) => (
                            <div className={classes.item} onClick={() => onSelected?.(options.map(() => value))}>
                                {icon} {label}
                            </div>
                        ))}
                    {multi && all && <hr />}
                    {search && (
                        <Input
                            theme='primary'
                            rootProps={{ className: classes.filter, onClick: e => e.preventDefault() }}
                            placeholder={searchLabel ?? t('label-search')}
                            left={<Search />}
                            onChange={e => setFilter(e.target.value)}
                        />
                    )}
                    {options
                        .map((option, i) => ({ option, i }))
                        .filter(({ option }) => !search || search(option).toLowerCase().includes(filter.toLowerCase()))
                        .map(({ option, i }) => (
                            <div
                                key={i}
                                className={clsx(classes.item, cls, { selected: selected[i] })}
                                onClick={e => {
                                    const newSelected = multi ? [...selected] : [];
                                    newSelected[i] = multi || unselect ? !selected[i] : true;
                                    props.onSelected?.(newSelected);
                                    if (multi) e.preventDefault();
                                }}
                            >
                                {multi && <Checkbox checked={!!selected[i]} />}
                                {render?.(option, 'popup') ?? String(option)}
                            </div>
                        ))}
                </Popover>
            }
        />
    );
};
